import React from 'react';

import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { Checkbox, Chip } from '@mui/material';
import { getGenotypeDisplayParts } from 'src/util/util';
import { TILE_COLOURS } from './tileColours';
import { useLegendNodeDrop } from './legendDrag/useLegendNodeDrop';
import { useLegendRecordWriter } from './legendDrag/useLegendRecordWriter';
import { RECORD_KIND } from './legendDrag/legendRecordPayload';

// Two layouts, one component. On the family card the legend is a horizontal
// strip of chips, where a 15px gap between items is right. In the legend
// CONFIG modal (showCheckboxes) each entry is its own row, and that same gap
// then lands between every row — which is what made the phenotype and
// genotype lists look padded out next to the compact Diseases list. So the
// checkbox layout is a tight column instead, at the same 4px the Diseases
// list uses in that mode (LegendMenu's Container gap).
const Container = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'rows'
})(({ rows }) =>
  rows
    ? { display: 'flex', flexDirection: 'column', gap: 4, flexWrap: 'nowrap' }
    : { display: 'flex', gap: 15, flexWrap: 'wrap' }
);

const BoxHolder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginRight: 10,

  fontSize: '1rem',
  '&[draggable="true"]': {
    cursor: 'grab'
  },
  '&[draggable="true"]:active': {
    cursor: 'grabbing'
  },
  '&.dragging': {
    opacity: 0.5
  }
});

// Genotypes carry no colour of their own, so the drag chip wears the genotype
// register the sidebar's record tiles already use — the purple family stays
// with genotypes (text 8.59:1 on its background).
const GENOTYPE_CHIP = {
  background: TILE_COLOURS.genotype.background,
  color: TILE_COLOURS.genotype.text,
  borderColor: TILE_COLOURS.genotype.border
};

/** What the row shows, so the chip under the cursor says the same thing. */
const genotypeLabel = (t, genotype) =>
  t(getGenotypeDisplayParts(genotype).symbol || genotype.name);

export default function GenotypesLegend({
  genotypes,
  id,
  dataset,
  setDataset,
  draggable,
  labelsSet,
  setLabelsSet,
  showCheckboxes = false,
  // Drag-to-assign only (the canvas legend). See PedigreeChart.
  setPedigreeState,
  dropBlocker
}) {
  const t = useAutoTranslation();

  const genotypeCases = React.useMemo(() => {
    const cases = {};
    (dataset || []).forEach((member) => {
      const fromGenotypes = member.genotypes || [];
      const fromDiagnoses = (member.diagnoses || []).filter((d) =>
        d.id?.startsWith('NCBIGene:')
      );
      const seen = new Set();
      [...fromGenotypes, ...fromDiagnoses].forEach((ge) => {
        if (seen.has(ge.id)) return;
        seen.add(ge.id);
        cases[ge.id] = cases[ge.id] === undefined ? 1 : cases[ge.id] + 1;
      });
    });
    return cases;
  }, [dataset]);

  const { checkTarget, write } = useLegendRecordWriter({
    setDataset,
    setPedigreeState,
    dropBlocker,
    enabled: Boolean(draggable)
  });

  const { draggedId, rowDragProps } = useLegendNodeDrop({
    enabled: Boolean(draggable),
    memberRecords: dataset,
    chipFor: (genotype) => ({
      label: genotypeLabel(t, genotype),
      ...GENOTYPE_CHIP
    }),
    // Always a genotype, whatever its id looks like: inferring it from the
    // prefix posted a gene without an `NCBIGene:` id to /diagnoses, which stored
    // it on the person as an affected disease (see recordKindOf).
    onDrop: (genotype, target) => {
      const kind = RECORD_KIND.genotype;
      const member = checkTarget(genotype, target, kind);
      if (member) write({ member, item: genotype, kind });
    }
  });

  const handleGenotypeToggle = (id) => {
    setLabelsSet((prev) => ({
      ...prev,
      genotypesMap: {
        ...prev.genotypesMap,
        [id]: !prev.genotypesMap?.[id]
      }
    }));
  };

  return (
    <>
      <Grid>
        <Container container id={id} rows={showCheckboxes ? 1 : 0}>
          {genotypes
            .filter(
              (el) =>
                showCheckboxes ||
                !labelsSet?.genotypesMap ||
                labelsSet.genotypesMap[el.id] !== false
            )
            .map((el, i) => {
              const isDragging = draggedId === el.id;

              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                >
                  {labelsSet && showCheckboxes && (
                    <Checkbox
                      checked={labelsSet?.genotypesMap?.[el.id] ?? true}
                      onChange={() => handleGenotypeToggle(el.id)}
                      size="small"
                    />
                  )}
                  <BoxHolder
                    {...rowDragProps(el)}
                    className={isDragging ? 'dragging' : ''}
                    title={
                      draggable
                        ? t('Drag {{name}} to assign to a family member', {
                            name: el.name
                          })
                        : undefined
                    }
                  >
                    <span style={{ fontSize: '1rem' }}>
                      {genotypeLabel(t, el)}{' '}
                      <span style={{ fontSize: '0.85rem', color: '#757575' }}>
                        ({el.id})
                      </span>
                      <Chip
                        label={t('{{value}} cases', {
                          value: genotypeCases[el.id] ?? 0
                        })}
                        size={'small'}
                        sx={{
                          fontStyle: 'italic',
                          fontSize: 11,
                          mx: 1,
                          height: 1
                        }}
                      />
                    </span>
                  </BoxHolder>
                </div>
              );
            })}
        </Container>
      </Grid>
    </>
  );
}
